/**
 * Employee types - Updated to match new schema
 */

export enum EmployeeType {
  FULL_TIME = 'full-time',
  CONTRACTOR = 'contractor',
  SUB_CONTRACTOR = 'sub-contractor',
  INTERN = 'intern',
}

export enum LocationType {
  ONSHORE = 'onshore',
  OFFSHORE = 'offshore',
}

export enum EmploymentStatus {
  ACTIVE = 'active',
  ON_LEAVE = 'on_leave',
  TERMINATED = 'terminated',
}

export enum PayType {
  HOURLY = 'hourly',
  SALARY = 'salary',
  CONTRACT = 'contract',
}

export enum OnboardingStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ON_HOLD = 'on_hold',
}

export enum OffboardingStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ON_HOLD = 'on_hold',
}

export enum TeamDepartment {
  AI = 'AI',
  DATA = 'Data',
  BI = 'BI',
  CUSTOM = 'Custom',
}

export interface Employee {
  employee_id: number;
  user_id?: number;

  // Basic Information
  first_name: string;
  middle_name?: string;
  last_name: string;

  // Contact Information
  personal_email?: string;
  work_email?: string;
  phone?: string;

  // Address Information
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;

  // Employment Details
  location_type: LocationType;
  employee_type: EmployeeType;
  employment_status: EmploymentStatus;
  pay_type?: PayType;
  vendor?: string;

  // Dates
  hire_date: string;
  end_date?: string;

  // Organizational Structure
  manager_id?: number;
  role?: string;
  team?: TeamDepartment | string;
  custom_team?: string;
  profile_picture?: string;  // Azure ADLS Gen2 URL

  // Skills and Experience
  expertise?: string;
  comments?: string;

  // Status
  is_active: boolean;

  // Relationships (optional - loaded separately)
  onboarding?: EmployeeOnboarding;
  project_allocations?: ProjectAllocation[];

  // Calculated field (from backend)
  total_allocation_percentage?: number;
}

export interface EmployeeOnboarding {
  onboarding_id: number;
  employee_id: number;
  onboarding_status: OnboardingStatus;
  onboarding_start_date?: string;
  onboarding_completed_date?: string;
  // New IT checklist fields
  work_email_created: boolean;
  outlook_configured: boolean;
  teams_configured: boolean;
  laptop_assigned: boolean;
  wifi_configured: boolean;
  other_checklist?: string;
  // Legacy fields (kept for backward compatibility)
  it_setup_completed: boolean;
  documentation_completed: boolean;
  training_completed: boolean;
}

export interface OnboardingChecklist {
  work_email_created: boolean;
  outlook_configured: boolean;
  teams_configured: boolean;
  laptop_assigned: boolean;
  wifi_configured: boolean;
  other_checklist?: string;
  // Legacy fields
  it_setup_completed?: boolean;
  documentation_completed?: boolean;
  training_completed?: boolean;
}

export interface ProjectAllocation {
  employee_id: number;
  project_id: number;
  allocation_percentage: number;
  start_date?: string;
  end_date?: string;
  role_on_project?: string;
  is_active: boolean;

  // Additional fields from API
  employee_name?: string;
  project_name?: string;
  client_name?: string;  // Client name from project
  project_end_date?: string;  // Project's actual end date (includes extensions)
}

export interface EmployeeCreate {
  user_id?: number;

  // Basic Information
  first_name: string;
  middle_name?: string;
  last_name: string;

  // Contact Information
  personal_email?: string;
  work_email?: string;
  phone?: string;

  // Address Information
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;

  // Employment Details
  location_type: LocationType;
  employee_type: EmployeeType;
  employment_status?: EmploymentStatus;
  pay_type?: PayType;
  vendor?: string;

  // Dates
  hire_date: string;
  end_date?: string;

  // Organizational Structure
  manager_id?: number;
  role?: string;

  // Skills and Experience
  expertise?: string;
  comments?: string;

  // Status
  is_active?: boolean;
}

export interface EmployeeUpdate {
  // Basic Information
  first_name?: string;
  middle_name?: string;
  last_name?: string;

  // Contact Information
  personal_email?: string;
  work_email?: string;
  phone?: string;

  // Address Information
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;

  // Employment Details
  location_type?: LocationType;
  employee_type?: EmployeeType;
  employment_status?: EmploymentStatus;
  pay_type?: PayType;
  vendor?: string;

  // Dates
  hire_date?: string;
  end_date?: string;

  // Organizational Structure
  manager_id?: number;
  role?: string;

  // Skills and Experience
  expertise?: string;
  comments?: string;

  // Status
  is_active?: boolean;
}
